import type { ReactNode } from "react";
import { Link } from "react-router";

import {
  classifyInventoryReadFailure,
  useClinicalInventoryQuery,
} from "@/api/clinicalInventory";
import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { queryKeys } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  usePreviewCandidatesQuery,
  type CandidateSummary,
} from "@/api/preview";
import { formatDateTime, shortHash } from "@/lib/format";
import {
  ACTIVATION_LABELS,
  AVAILABILITY_LABELS,
  BOUNDARY_OWNER_LABELS,
  BOUNDARY_SUBJECT_LABELS,
  EDITABILITY_LABELS,
  EDITABILITY_REASON_LABELS,
  ORIGIN_LABELS,
  ORIGIN_TONES,
  PRESENCE_LABELS,
  SETTING_LABELS,
  SETTING_STATE_LABELS,
  SETTING_STATE_TONES,
  SUPERSESSION_REASON_LABELS,
  componentDetailTarget,
  componentSubjects,
  componentTitle,
  effectiveSourceLabels,
  groupComponentsBySection,
  groupedComponentCount,
  isUnnamed,
  libraryHeadNote,
  settingDetailTarget,
  sourcePathSummary,
  sourceSummary,
  type ClinicalComponent,
  type ClinicalInventory,
  type ClinicalInventoryIdentity,
  type ClinicalSetting,
} from "@/lib/clinicalInventory";
import { ASSET_TYPE_LABELS } from "@/lib/librarySections";
import { Badge } from "@/ui/Badge";
import { Notice } from "@/ui/Notice";
import { CatalogDetails, CatalogMeta } from "@/ui/library/CatalogRow";
import { ProjectSectionPane } from "@/ui/project/ProjectSectionPane";
import { EmptyState, ErrorState, StaleDataNotice } from "@/ui/states";

/**
 * Content sources — where every piece of this project's clinical content comes
 * from (#1086).
 *
 * ## THE GAP IT CLOSES
 *
 * Every other Project section enumerates the seed CATALOG's entries and the
 * project's pins, so content the mounted base ships that the catalog does not
 * describe is real at runtime and invisible on screen. Measured on the real
 * default pack, that is 23 of 38 components — `pathway:depression`,
 * `pathway:ocd`, five anxiety sub-evaluations, both eating-disorder variants and
 * twelve Summary targets. A clinician could not tell whether an unlisted
 * component was inherited, absent or disabled, which is the verified gap the
 * ticket names.
 *
 * `GET /v1/projects/{id}/clinical-inventory` (#1085) answers that from the
 * MERGED TREE the pinned engine produced, and this surface renders it.
 *
 * ## AN ADDITION INSIDE THE ESTABLISHED LAYOUT, NOT A DASHBOARD
 *
 * It is a destination in the existing Clinical configuration rail, reached by
 * the same `projection: "none"` mechanism `questionnaires` (#489) and
 * `closing` (#867) already use — `content-sources` is deliberately not a member
 * of the contract's `SectionSlug`, and `SectionSurface`'s guard exists to stop a
 * rail slug reaching `getProjectSection` on the strength of its name.
 *
 * Inside, it is the same three parts every Project section has: the
 * `ProjectSectionPane` shell, compact rows that put each component's facts on
 * one line, and a collapsed `Details` disclosure per row for the technical
 * identities. Nothing here is a JSON viewer and nothing here is a second
 * navigation: every row that has a supported destination links to the EXISTING
 * named route that owns it.
 *
 * ## IT DECIDES NOTHING AND WRITES NOTHING
 *
 * Provenance, presence, runtime activation and editability are all server
 * facts. `lib/clinicalInventory.ts` maps each contract enum to a sentence and
 * does not compare a single value; there is no merge logic here and no
 * screening-rule semantics (#1076 owns those). The surface offers no control
 * that mutates: viewing it creates no asset, pin, version or session.
 */
export function ProjectContentSources({
  projectId,
  sectionLabel,
}: {
  projectId: string;
  sectionLabel: string;
}) {
  const inventory = useClinicalInventoryQuery(projectId);
  const candidates = usePreviewCandidatesQuery(projectId);

  // NOTHING FROM A PREVIOUS VISIT IS SHOWN AS THIS ONE'S ANSWER.
  //
  // `isFetchedAfterMount` is the exact primitive: false until a fetch started
  // by THIS mount has settled. Without the gate, TanStack hands a remount the
  // previous response — `isPending: false`, old `data`, `isFetching: true` —
  // and since mutations deliberately do not invalidate this key, a clinician
  // returning after changing a pin would read origins from before the change
  // under a heading that says "Current configuration".
  //
  // `gcTime: 0` on the query is not sufficient on its own and this is not
  // belt-and-braces: cache eviction is SCHEDULED, so a remount in the same tick
  // still finds the entry. Measured — the test for this failed against
  // `gcTime: 0` alone. The eviction is kept because never reusing the entry is
  // the honest lifetime for this read; the gate is what makes it a guarantee.
  //
  // THE SHELL SURVIVES A DEGRADED READ (#909 round 2, as `ProjectClosingConversation`
  // applies it): a bare query state would take the section's accessible heading
  // off the screen while the inventory loads or after it fails.
  if (inventory.isPending || !inventory.isFetchedAfterMount) {
    return (
      <ProjectSectionPane sectionLabel={sectionLabel}>
        <QueryPendingState
          isPaused={inventory.isPaused}
          failureReason={inventory.failureReason}
          label="Loading where this project's clinical content comes from"
          errorTitle="This project's content sources could not be loaded"
          rows={4}
          queryKey={queryKeys.projectClinicalInventory(projectId)}
        />
      </ProjectSectionPane>
    );
  }

  // WHICH FAILURES MAY LEAVE THE LAST ANSWER ON SCREEN, AND WHICH MAY NOT.
  //
  // A transient failure (a 503 from the composition environment, a dropped
  // connection) leaves a response that was true about one identity, so it stays
  // with an explicit stale notice. A TERMINAL one must take it away, and for
  // this surface the set is wider than the usual 401/403/404: `queryClient`
  // classifies a 409 `conflict` as terminal too, and that is exactly right
  // here. This endpoint's 409 means the configuration moved while the response
  // was being composed — so the answer already on screen describes a
  // configuration that no longer exists, and leaving it up is precisely the
  // "reloading cannot revert the screen to stale cached origins" failure.
  const terminal = inventory.isError && isTerminalQueryError(inventory.error);
  const readable = terminal ? undefined : inventory.data;

  if (inventory.isError && readable === undefined) {
    return (
      <ProjectSectionPane sectionLabel={sectionLabel}>
        <InventoryReadFailure
          error={inventory.error}
          onRetry={() => void inventory.refetch()}
        />
      </ProjectSectionPane>
    );
  }

  const data = readable as ClinicalInventory;
  const groups = groupComponentsBySection(data.components);
  const subjects = componentSubjects(data.components);
  // The partition is total by construction; this is the assertion that it
  // STAYED total. A row lost between the response and the screen is the one
  // failure this surface must never produce quietly.
  const dropped = data.components.length - groupedComponentCount(groups);

  return (
    <ProjectSectionPane
      sectionLabel={sectionLabel}
      controls={
        <>
          {inventory.isError ? (
            <StaleDataNotice
              updatedAt={inventory.dataUpdatedAt}
              onRetry={() => void inventory.refetch()}
            />
          ) : inventory.isFetching ? (
            /*
              A RE-READ IN FLIGHT OVER A RENDERED ANSWER, SAID OUT LOUD.
              `gcTime: 0` on the query means a REMOUNT has nothing cached to
              paint, so this is only ever a refresh inside one mount — a manual
              retry, or a window-focus refetch. Even then the rows below are the
              previous read, and the heading above them says "Current
              configuration", so the two must not be left to look identical
              while the answer is still arriving.
            */
            <Notice role="status">
              Re-reading this project&apos;s configuration. Everything below is
              the previous read until it settles.
            </Notice>
          ) : null}
          <ConfigurationHeading
            identity={data.identity}
            languages={data.languages}
            componentCount={data.components.length}
          />
          <FrozenTestVersions projectId={projectId} candidates={candidates} />
          {data.truncated ? (
            <Notice>
              This project holds more clinical content than one response carries.
              The list below is the first part of it, in order, and is not the
              whole configuration.
            </Notice>
          ) : null}
          {dropped > 0 ? (
            <Notice>
              {dropped} component(s) the Editor reported are not shown below.
              This screen is incomplete; treat it as a failed read rather than
              as this project&apos;s configuration.
            </Notice>
          ) : null}
        </>
      }
      details={<InventoryIdentityDetails identity={data.identity} data={data} />}
    >
      {/*
        THE EMPTY STATE BELONGS TO THE COMPONENT LIST, NOT TO THE RESPONSE.
        `components`, `settings` and `boundaries` are three independent sections
        of one response, and the service supplies the ownership boundaries for
        every project. Hanging all three off the component count meant a valid
        empty-component response suppressed the typed Project decisions AND the
        outside-the-Editor ownership statement this surface is required to show.
      */}
      <div className="space-y-6">
        {data.components.length === 0 ? (
          <EmptyState
            title="No clinical content was reported"
            description="The Editor composed this project's configuration and found no clinical components in it. That is a real answer about the project, not a failed read."
          />
        ) : (
          groups.map((group) => (
            <section
              key={group.section ?? "unsectioned"}
              aria-labelledby={`content-sources-${group.section ?? "unsectioned"}`}
              className="rounded-md border border-border bg-card"
            >
              {/*
                A HEADING, AND DELIBERATELY NOT A LINK TO THE SECTION IT NAMES.
                It would be one line to make `PATHWAYS` navigate to
                `/config/pathways`, and it would be the wrong line: the contract
                says `section` is "a grouping, not a promise about where its
                values can be read", and on the real default pack the Pathways
                section lists NONE of the inherited pathways grouped here —
                which is the gap this screen exists to show. A heading that
                looked like a way to reach these rows would promise exactly the
                thing that is missing. Where a real destination exists, the ROW
                carries it, from `detail`.
              */}
              <h3
                id={`content-sources-${group.section ?? "unsectioned"}`}
                className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <span>{group.label}</span>
                <span aria-hidden="true" className="text-border">
                  ·
                </span>
                <span className="font-normal normal-case tracking-normal">
                  {group.components.length} component
                  {group.components.length === 1 ? "" : "s"}
                </span>
              </h3>
              <ul>
                {group.components.map((component) => (
                  <ComponentRow
                    key={component.key}
                    projectId={projectId}
                    component={component}
                    subject={subjects.get(component.key) ?? component.key}
                  />
                ))}
              </ul>
            </section>
          ))
        )}

        <ProjectSettings projectId={projectId} settings={data.settings} />
        <OwnershipBoundaries boundaries={data.boundaries} />
      </div>
    </ProjectSectionPane>
  );
}

/**
 * The failed read, in the terms the route actually declares.
 *
 * `QueryErrorState` covers denial, the retired 501 and the generic transient
 * case. What it cannot say is the refusals specific to this endpoint, and
 * saying them wrongly is the defect. In particular THE THREE 409s NEED THREE
 * DIFFERENT SCREENS, because only one of them is cleared by pressing a button:
 *
 *  * the configuration moved mid-composition — retry, and say so;
 *  * a stored pin disagrees with the runtime — a retry cannot change that, so
 *    there is no retry, and the screen names the recovery instead;
 *  * the stored draft/adoption state cannot be composed — likewise, and the
 *    screen names the address the service pointed at.
 *
 * A 503 means the composition environment is down while the project's own
 * stored state is perfectly fine. None of these is "this project has no
 * clinical content", and the bounded scope forbids showing any of them as an
 * empty successful result.
 */
function InventoryReadFailure({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}) {
  const failure = classifyInventoryReadFailure(error);

  if (failure.kind === "moved") {
    // `ErrorState` DIRECTLY, and not through `QueryErrorState`, for one
    // reason: `isTerminalQueryError` classifies a 409 `conflict` as terminal,
    // so the shared component would withhold the retry — correctly for every
    // other surface, where a conflict means the caller must do something
    // different. This endpoint is the exception its own contract names: "the
    // project's configuration changed while this inventory was being composed;
    // re-read it". The identical request is the recovery, so a screen with no
    // button would strand a clinician on a refusal that one click clears.
    //
    // The terminal classification is still honoured where it matters — the
    // caller has already dropped the cached response rather than showing a
    // superseded configuration as current.
    return (
      <ErrorState
        title="This project changed while its content sources were being read"
        description="A pin, the draft, a governed setting or the final safety-questioning decision moved while this screen was being composed, so the Editor refused to describe one configuration under another's identity. Nothing is wrong with the project — read it again."
        onRetry={onRetry}
      />
    );
  }

  if (failure.kind === "identity_mismatch") {
    // NO RETRY. The service refuses BEFORE it composes anything, because the
    // project's stored base identity disagrees with the runtime it would be
    // read against — and an inventory of content the Editor cannot verify is
    // worse than no inventory. The identical request answers identically until
    // the stored identity is re-resolved, so a "Try again" here would be a
    // control whose only possible outcome is this same screen.
    return (
      <ErrorState
        title="This project's clinical content cannot be verified"
        description={
          failure.mismatch === "draft_base"
            ? "This project's draft was authored against a base pack that is no longer the project's current base, so the Editor will not describe content it cannot verify. Re-resolving the draft's base is the recovery; reading again is not."
            : failure.mismatch === "project_base"
              ? "This project's pinned base identity disagrees with the base pack the Editor has mounted, so it will not describe content it cannot verify. Re-resolving the project's base is the recovery; reading again is not."
              : "A stored identity for this project disagrees with the one the Editor resolved, so it will not describe content it cannot verify. Re-resolving that identity is the recovery; reading again is not."
        }
      />
    );
  }

  if (failure.kind === "uncomposable") {
    // NO RETRY, for the same reason and with a pointer. `field` is a path or an
    // asset id — never content — and it is the only thing that turns "this
    // project cannot be composed" into something a clinician can act on.
    return (
      <ErrorState
        title="This project's stored configuration could not be assembled"
        description={
          <>
            <span>
              The Editor could not compose this project&apos;s draft and adopted
              content into one configuration, so it has described none of it.
              This is a stored-state problem rather than a lost race: reading
              again answers the same way until it is corrected.
            </span>
            {failure.field === null ? null : (
              <span className="mt-2 block">
                First reported at{" "}
                <code className="font-mono text-xs">{failure.field}</code>.
              </span>
            )}
          </>
        }
      />
    );
  }

  if (failure.kind === "unavailable") {
    return (
      <QueryErrorState
        error={error}
        subject="Content sources"
        title="Where this project's content comes from could not be established"
        description="The Editor could not compose this project's configuration: the pinned engine or the pack it reads is unavailable. This project's own stored content is unaffected, and the reading is retryable."
        onRetry={onRetry}
      />
    );
  }

  return (
    <QueryErrorState
      error={error}
      subject="Content sources"
      title="This project's content sources could not be loaded"
      description="The Editor Service did not return where this project's clinical content comes from, so nothing on this screen can be treated as current."
      deniedDescription="This project's clinical configuration is outside your access."
      treatNotFoundAsDenied
      onRetry={onRetry}
    />
  );
}

/**
 * What this screen IS: the project's configuration as it stands now.
 *
 * Stated in words rather than implied by position, because the block beneath it
 * lists frozen test versions that are also real configurations — and the
 * bounded scope asks for the current one to be "clearly labelled … versus a
 * previous frozen testing version".
 */
function ConfigurationHeading({
  identity,
  languages,
  componentCount,
}: {
  identity: ClinicalInventoryIdentity;
  languages: readonly string[];
  componentCount: number;
}) {
  return (
    <div className="rounded-md border border-border bg-card px-5 py-3">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-foreground">
        <span>Current configuration</span>
        {/*
          "Not frozen", not "Live": this surface reads the project's own rows
          and knows nothing about any deployment. What the badge says is the
          real distinction from the block below — this configuration is still
          moving, and each frozen test version is a snapshot that is not.
        */}
        <Badge tone="accent">Not frozen</Badge>
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {componentCount} clinical component
        {componentCount === 1 ? "" : "s"}, read together at one moment. Every
        origin below was reported by the Editor against this one configuration —
        none of it is compared or guessed in the browser.
      </p>
      <CatalogMeta
        className="mt-2"
        items={[
          <span key="base">
            Base pack {identity.base.packName} {identity.base.packRevision}
          </span>,
          <span key="languages">
            Base languages: {languages.length > 0 ? languages.join(", ") : "none declared"}
          </span>,
        ]}
      />
    </div>
  );
}

/**
 * The project's frozen test versions, and whether each still describes the
 * configuration above.
 *
 * `freshness` IS THE SERVER'S READING and the browser never re-derives it. The
 * contract is explicit that a client must not infer currency from list
 * position, validity or expiry, and `CandidateSuperseded.reasons` names exactly
 * which of the identity coordinates moved — the same coordinates the inventory
 * response is itself bound to, which is what lets these two blocks be read
 * together.
 *
 * An absent `freshness` FAILS CLOSED: the contract says a client reading no
 * value "must treat the candidate as not current".
 */
function FrozenTestVersions({
  projectId,
  candidates,
}: {
  projectId: string;
  candidates: ReturnType<typeof usePreviewCandidatesQuery>;
}) {
  if (candidates.isPending) {
    return (
      <p className="px-1 text-sm text-muted-foreground">
        Checking this project&apos;s frozen test versions…
      </p>
    );
  }

  // A FAILED REFETCH KEEPS THE OLD ROWS, AND THOSE ROWS CARRY `freshness`.
  // TanStack leaves `data` in place when a refetch fails, so `isError` with
  // cached items is a real state — and rendering it unmarked would keep saying
  // "Matches the current configuration" about a reading the server has since
  // refused to confirm. Terminal failures drop the rows outright; retryable
  // ones keep them behind an explicit stale notice, exactly as the inventory
  // query above does.
  const terminal = candidates.isError && isTerminalQueryError(candidates.error);
  const readable = terminal ? undefined : candidates.data;

  if (candidates.isError && readable === undefined) {
    return (
      <Notice>
        This project&apos;s frozen test versions could not be read, so whether an
        earlier one still matches the configuration above is not known here.
      </Notice>
    );
  }

  const items = readable?.items ?? [];
  if (items.length === 0) {
    return (
      <p className="px-1 text-sm text-muted-foreground">
        No test version has been frozen for this project yet.
      </p>
    );
  }

  return (
    <>
      {candidates.isError ? (
        <StaleDataNotice
          updatedAt={candidates.dataUpdatedAt}
          onRetry={() => void candidates.refetch()}
        />
      ) : null}
      <section
        aria-labelledby={`frozen-versions-${projectId}`}
        className="rounded-md border border-border bg-card"
      >
        <h3
          id={`frozen-versions-${projectId}`}
          className="border-b border-border px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Test versions
        </h3>
        <ul>
          {items.map((candidate) => (
            <FrozenTestVersionRow
              key={candidate.candidateId}
              candidate={candidate}
            />
          ))}
        </ul>
      </section>
    </>
  );
}

function FrozenTestVersionRow({ candidate }: { candidate: CandidateSummary }) {
  const freshness = candidate.freshness ?? null;
  const current = freshness?.state === "current";
  const reasons =
    freshness?.state === "superseded"
      ? freshness.reasons.map((reason) => SUPERSESSION_REASON_LABELS[reason])
      : [];

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-5 py-2.5 last:border-b-0">
      <div className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            {candidate.revision === null || candidate.revision === undefined
              ? "Test version"
              : `Test version ${candidate.revision.number}`}
          </span>
          {current ? (
            <Badge tone="accent">Matches the current configuration</Badge>
          ) : (
            <Badge tone="neutral">Previous</Badge>
          )}
        </span>
        <CatalogMeta
          items={[
            <span key="frozen">
              Frozen {formatDateTime(candidate.createdAt)}
            </span>,
            current ? null : (
              <span key="why">
                {reasons.length > 0
                  ? `No longer current because ${reasons.join(", ")}.`
                  : "The Editor did not report whether this still matches, so it is not treated as current."}
              </span>
            ),
          ]}
        />
      </div>
    </li>
  );
}

/**
 * One component: its origin, its three other facts, and its technical identity
 * behind a disclosure.
 *
 * NOT `DataRow`/`CatalogRow`, and the reason is structural rather than
 * stylistic: both require a `to` — their whole guarantee is a real `<a>`
 * stretched across the row — and a third of these components have no
 * destination at all (`detail.kind: "none"` is a real answer by contract, not a
 * gap). A row whose surface navigated to a guessed address would be exactly the
 * unsupported editing path the bounded scope forbids. So the row is built from
 * the same parts at the same size, with the destination as an explicit trailing
 * link where one exists and a stated reason where one does not.
 */
function ComponentRow({
  projectId,
  component,
  subject,
}: {
  projectId: string;
  component: ClinicalComponent;
  subject: string;
}) {
  const title = componentTitle(component);
  const origin = ORIGIN_LABELS[component.supply];
  const target = componentDetailTarget(projectId, component);
  const activation = ACTIVATION_LABELS[component.activation];
  const editability = EDITABILITY_LABELS[component.editability];
  const presence = PRESENCE_LABELS[component.presence];

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-5 py-2.5 last:border-b-0">
      <div className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          {/*
            AN UNNAMED COMPONENT'S TITLE IS AN IDENTIFIER, AND LOOKS LIKE ONE.
            `Mania` is a name the catalog supplies; `diagnostic_findings/mania/mania`
            is the server-owned coordinate reported because the catalog supplies
            none. Rendering the two identically would let a clinician read a
            coordinate as a product name — so the second is set in the same
            monospace this screen uses for every other identity, and the
            metadata line says outright that the catalog does not name it.
          */}
          <span
            className={
              isUnnamed(component)
                ? "font-mono text-sm font-semibold text-foreground"
                : "text-sm font-semibold text-foreground"
            }
          >
            {title}
          </span>
          {/*
            THE ORIGIN, ALWAYS AS A WORD. Tone is decoration over the label so
            the four origins stay distinguishable without colour — and `Mixed`,
            `Not supplied` and `Source not established` are three separate
            answers rather than degrees of one.
          */}
          <Badge tone={ORIGIN_TONES[component.supply]}>{origin.label}</Badge>
          {component.presence === "present" ? null : (
            <Badge tone="warning">{presence.label}</Badge>
          )}
        </span>
        <CatalogMeta
          items={[
            // The family, EXCEPT when the title already is it. A component
            // whose key admits no coordinate and whose catalog row is missing
            // is titled by its family's product name (`Closing conversation`),
            // and repeating that word two inches to the right says nothing and
            // reads as two different facts.
            ASSET_TYPE_LABELS[component.family] === title ? null : (
              <span key="family">{ASSET_TYPE_LABELS[component.family]}</span>
            ),
            <SourceFacts key="source" component={component} />,
            <span key="activation">{activation.label}</span>,
            component.editability === "editable" ? null : (
              <span key="editability">{editability.label}</span>
            ),
            component.availability === "supported" ? null : (
              <span key="availability">
                {AVAILABILITY_LABELS[component.availability]}
              </span>
            ),
            isUnnamed(component) ? (
              <span key="unnamed">Not named by the library catalog</span>
            ) : null,
          ]}
        />
      </div>

      {/*
        THREE OUTCOMES, NOT TWO. A link where an existing page serves this
        component's values; a stated refusal where the project runs an exact
        version no page serves; and the contract's own `none` everywhere else.
      */}
      {target === null ? (
        <span className="shrink-0 text-sm text-muted-foreground">
          No detail view
        </span>
      ) : target.kind === "pinned-version" ? (
        <span className="shrink-0 text-sm text-muted-foreground">
          No view for {target.version}
        </span>
      ) : (
        <RowLink
          href={target.href}
          label={
            target.kind === "library-asset" ? "Open in library" : "Open section"
          }
          subject={subject}
        />
      )}

      <CatalogDetails subject={subject}>
        <ComponentDetails component={component} target={target} />
      </CatalogDetails>
    </li>
  );
}

/**
 * A row's trailing destination, named for the row it belongs to.
 *
 * THE ACCESSIBLE NAME IS WRITTEN ONCE, in a single `sr-only` span, rather than
 * assembled from a painted word plus a hidden suffix. `CatalogDetails` records
 * the mechanism and it applies identically here: accessible-name computation
 * TRIMS each text node and concatenates inline content with no separator, so
 * `Open section` + ` for Mania` computes as "Open sectionfor Mania". A page of
 * links all named "Open section" is the other half of the same defect, which is
 * why the subject travels with every one of them.
 *
 * WCAG 2.5.3 is satisfied because the visible word is a prefix of the
 * accessible name, not a different string from it.
 */
function RowLink({
  href,
  label,
  subject,
}: {
  href: string;
  label: string;
  subject: string;
}) {
  return (
    <Link
      to={href}
      className="shrink-0 rounded-md px-2 py-1 text-sm font-medium text-foreground underline-offset-4 hover:underline"
    >
      <span aria-hidden="true">{label}</span>
      <span className="sr-only">{`${label} for ${subject}`}</span>
    </Link>
  );
}

/**
 * The row's source fact: which exact version supplies this component TODAY.
 *
 * Every decision is `lib/clinicalInventory.ts`'s — `effectiveSourceLabels`
 * selects by `supply` (never by which source rows happen to be present, and
 * never by `adoptedVersion`, both of which stay populated for layers that no
 * longer supply anything), and `libraryHeadNote` adds the head only where a pin
 * really is the supplying layer. This renders what they return.
 */
function SourceFacts({ component }: { component: ClinicalComponent }): ReactNode {
  const labels = effectiveSourceLabels(component);
  const head = libraryHeadNote(component);

  if (labels.length === 0) {
    // `absent` and `unresolved` have something to say; a supplying layer with
    // no source row is a disagreement inside the response, and the row stays
    // silent about a version rather than quoting another layer's.
    if (component.supply === "absent") {
      return <span>No source supplies it</span>;
    }
    if (component.supply === "unresolved") {
      return <span>Layers could not be attributed</span>;
    }
    return null;
  }

  // `From` prefixes the whole fact rather than each label, so a `mixed` origin
  // reads as one sentence — "From default 2026.09.1 + Hebrew rules v3" — and a
  // single source keeps the wording it had.
  return (
    <span>
      From {labels.join(" + ")}
      {head === null ? "" : ` · ${head}`}
    </span>
  );
}

/**
 * The collapsed technical evidence for one row.
 *
 * Everything a support engineer needs and a clinical author does not: the
 * stable key, the placement coordinate, each contributing layer at its exact
 * identity with its supplied/removed file counts, and the per-ROLE breakdown —
 * which is where a partially-adopted component becomes visible, because a role
 * the tree does not hold is reported `absent` rather than omitted.
 */
function ComponentDetails({
  component,
  target,
}: {
  component: ClinicalComponent;
  target: ReturnType<typeof componentDetailTarget>;
}) {
  const activation = ACTIVATION_LABELS[component.activation];
  const presence = PRESENCE_LABELS[component.presence];
  const origin = ORIGIN_LABELS[component.supply];
  const editability = EDITABILITY_LABELS[component.editability];
  const reason = component.editabilityReason ?? null;

  return (
    <div className="space-y-3 pb-3 text-sm text-muted-foreground">
      <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[10rem_1fr]">
        <dt className="font-medium text-foreground">Component key</dt>
        <dd className="font-mono text-xs">{component.key}</dd>

        <dt className="font-medium text-foreground">Placement</dt>
        <dd className="font-mono text-xs">
          {component.coordinate ?? "no coordinate for this family"}
        </dd>

        <dt className="font-medium text-foreground">Origin</dt>
        <dd>{origin.explanation}</dd>

        <dt className="font-medium text-foreground">Content present</dt>
        <dd>{presence.explanation}</dd>

        <dt className="font-medium text-foreground">Runtime</dt>
        <dd>{activation.explanation}</dd>

        <dt className="font-medium text-foreground">Editing</dt>
        <dd>
          {editability.explanation}
          {reason === null ? "" : ` ${EDITABILITY_REASON_LABELS[reason]}`}
        </dd>

        <dt className="font-medium text-foreground">Values</dt>
        <dd>
          {target === null
            ? "No existing view serves this component's values. The Editor names no read rather than naming one that answers something else."
            : target.kind === "library-asset"
              ? `The library page for ${target.assetId}, which serves ${target.version} — the exact version this project pins.`
              : target.kind === "pinned-version"
                ? `This project runs ${target.assetId} at ${target.version}. The library page serves ${target.head ?? "a version the Editor could not name"}, which is not what this project runs, so no link is offered.`
                : `The ${target.section} section of this project.`}
        </dd>
      </dl>

      <div>
        <p className="font-medium text-foreground">Sources</p>
        {component.sources.length === 0 ? (
          <p>No layer supplies this component.</p>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {component.sources.map((source) => (
              <li key={`${source.kind}:${source.ref}:${source.version ?? ""}`}>
                {sourceSummary(source)} — {sourcePathSummary(source)}
                {source.contentSha256 === null ||
                source.contentSha256 === undefined ? null : (
                  <span className="font-mono text-xs">
                    {" "}
                    {shortHash(source.contentSha256)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="font-medium text-foreground">File roles</p>
        {component.roles.length === 0 ? (
          <p>
            No roles were reported. For a component whose location could not be
            resolved this is the honest answer, not an empty component.
          </p>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {component.roles.map((role) => (
              <li key={role.role}>
                <span className="font-mono text-xs">{role.role}</span> —{" "}
                {ORIGIN_LABELS[role.supply].label},{" "}
                {PRESENCE_LABELS[role.presence].label.toLowerCase()}
                {role.languages.length > 0
                  ? `, languages: ${role.languages.join(", ")}`
                  : ", no language directory holds it"}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * The typed Project decisions that are clinical configuration but not pack
 * components.
 *
 * Listed beside the components rather than hidden with them, because a
 * clinician asking "what does this project run" is asking about both — and each
 * is read from the row that OWNS it, never off the merged tree, so an authored
 * decision can never be reported here as inherited.
 */
function ProjectSettings({
  projectId,
  settings,
}: {
  projectId: string;
  settings: readonly ClinicalSetting[];
}) {
  if (settings.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="content-sources-settings"
      className="rounded-md border border-border bg-card"
    >
      <h3
        id="content-sources-settings"
        className="border-b border-border px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
      >
        Project decisions
      </h3>
      <ul>
        {settings.map((setting) => {
          const label = SETTING_LABELS[setting.key];
          const state = SETTING_STATE_LABELS[setting.state];
          const target = settingDetailTarget(projectId, setting);
          const editability = EDITABILITY_LABELS[setting.editability];

          return (
            <li
              key={setting.key}
              className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-5 py-2.5 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    {label.label}
                  </span>
                  <Badge tone={SETTING_STATE_TONES[setting.state]}>
                    {state.label}
                  </Badge>
                </span>
                <CatalogMeta
                  items={[
                    <span key="what">{label.explanation}</span>,
                    setting.value === null || setting.value === undefined ? null : (
                      <span key="value">Resolves to {setting.value}</span>
                    ),
                    setting.editability === "editable" ? null : (
                      <span key="editability">{editability.label}</span>
                    ),
                  ]}
                />
              </div>
              {target === null ? (
                <span className="shrink-0 text-sm text-muted-foreground">
                  No control
                </span>
              ) : (
                <RowLink
                  href={target.href}
                  label="Open section"
                  subject={label.label}
                />
              )}
              <CatalogDetails subject={label.label}>
                <p className="pb-3 text-sm text-muted-foreground">
                  {state.explanation} {editability.explanation}
                </p>
              </CatalogDetails>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Configuration a clinician can SEE named here and can never edit here.
 *
 * Published rather than omitted, which is the prerequisite's own reasoning: an
 * inventory that left these out would read as "the Editor controls everything
 * it lists". The bounded scope says the same from the other side — keep
 * deployment-owned settings visibly outside this editing surface.
 */
function OwnershipBoundaries({
  boundaries,
}: {
  boundaries: ClinicalInventory["boundaries"];
}) {
  if (boundaries.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="content-sources-boundaries"
      className="rounded-md border border-border bg-card"
    >
      <h3
        id="content-sources-boundaries"
        className="border-b border-border px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
      >
        Owned outside the Editor
      </h3>
      <ul>
        {boundaries.map((boundary) => (
          <li
            key={boundary.subject}
            className="border-b border-border px-5 py-2.5 last:border-b-0"
          >
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-foreground">
                {BOUNDARY_SUBJECT_LABELS[boundary.subject]}
              </span>
              <Badge tone="outline">
                {BOUNDARY_OWNER_LABELS[boundary.owner]}
              </Badge>
            </span>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {boundary.reason}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The pane-level `Details`: the ONE configuration identity every fact above was
 * read against.
 *
 * Collapsed, because these are the digests a support engineer quotes and not
 * something a clinical author reads while looking at a pathway. Kept rather
 * than dropped, because two responses are the same configuration exactly when
 * their `revision` matches — and the coordinates beneath it say WHICH one moved
 * when they do not.
 */
function InventoryIdentityDetails({
  identity,
  data,
}: {
  identity: ClinicalInventoryIdentity;
  data: ClinicalInventory;
}) {
  return (
    <dl className="grid gap-x-4 gap-y-1 text-sm text-muted-foreground sm:grid-cols-[14rem_1fr]">
      <dt className="font-medium text-foreground">Configuration revision</dt>
      <dd className="font-mono text-xs">{identity.revision}</dd>

      <dt className="font-medium text-foreground">Base pack</dt>
      <dd className="font-mono text-xs">
        {identity.base.packName} {identity.base.packRevision}
        {identity.base.contentSha256 === null ||
        identity.base.contentSha256 === undefined
          ? ""
          : ` · ${shortHash(identity.base.contentSha256)}`}
      </dd>

      <dt className="font-medium text-foreground">Draft version</dt>
      <dd className="font-mono text-xs">{identity.draftVersionId}</dd>

      <dt className="font-medium text-foreground">Adoption pin set</dt>
      <dd className="font-mono text-xs">
        {shortHash(identity.adoptionsDigest)} · token{" "}
        {identity.adoptionsVersion}
      </dd>

      <dt className="font-medium text-foreground">Governed settings</dt>
      <dd className="font-mono text-xs">{shortHash(identity.governedDigest)}</dd>

      <dt className="font-medium text-foreground">
        Final questioning state version
      </dt>
      <dd className="font-mono text-xs">
        {identity.finalQuestioningStateVersion}
      </dd>

      <dt className="font-medium text-foreground">Response completeness</dt>
      <dd>
        {data.truncated
          ? "Truncated — this project holds more components than one response carries."
          : "Complete — every component the Editor composed is listed."}
      </dd>
    </dl>
  );
}
